import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, resolveFormattingLocale } from '@eis/core';
import {
  ConfirmAction,
  EffectiveDateSelector,
  FormSection,
  cardSurfaceSx,
  PageHeader,
  PageLayout,
} from '@eis/patterns';
import { useAvailableGroupTarifs, useCreateGroupPolicy } from '../api/group-policies';
import { translateValidationMessage } from '../../../i18n/validation';
import {
  POLICYHOLDER_ROLE,
  linksOf,
  partnerRefusalOf,
  roleLabel,
} from '../../policies/model/partners';
import { PartnerSection } from '../../policies/ui/PartnerSection';
import {
  STEP_FIELDS,
  WIZARD_STEPS,
  createGroupPolicySchema,
  defaultCreateGroupPolicyForm,
  type CreateGroupPolicyForm,
  type WizardStep,
} from '../model/wizard';

/**
 * Create wizard: policyholder → group tarif and allowed tarifs → billing → summary.
 *
 * The step is only advanced after the fields that step owns validate, so an error is shown next
 * to the control that caused it rather than as a wall of messages at the end. The finish action
 * is ConfirmAction-guarded: creating a Gruppen-Police is a real commitment, not a save.
 *
 * # Two things this wizard deliberately does not do
 *
 * It does not offer a partner PICKER. Partner is an existing domain this platform does not own
 * and cannot list (SSOT §4.2), so the honest control is the id — inventing a dropdown over
 * partners nobody can enumerate would be a picker that is empty in every real deployment.
 *
 * It does not name a VERSION. The operator picks the group TARIF; the service freezes its
 * active version onto the agreement (architecture §A.5). That is the whole of I-6 on this screen:
 * a product is a choice, a version is not.
 *
 * And it reads NO configuration hook. `useAvailableGroupTarifs` is an instance read (prompt
 * 14.6): granting this persona a configuration grant would not have filled these two controls,
 * it would have put the whole configuration surface in their sidebar, because nav entries and
 * route guards share one declaration (Addendum A.2). See OQ-11.
 */
export function GroupPolicyWizard({
  onCreated,
  onCancel,
}: {
  readonly onCreated: (groupPolicyId: string) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const [stepIndex, setStepIndex] = useState(0);
  const [confirming, setConfirming] = useState(false);

  // ONE read, on the instance api, for both things creating an agreement has to name. It
  // answers only about group tarifs an agreement can actually be created under, so the picker
  // cannot offer something the finish action would then be refused for.
  const available = useAvailableGroupTarifs();
  const create = useCreateGroupPolicy();

  const form = useForm<CreateGroupPolicyForm>({
    resolver: zodResolver(createGroupPolicySchema),
    defaultValues: defaultCreateGroupPolicyForm,
    mode: 'onTouched',
  });

  const step: WizardStep = WIZARD_STEPS[stepIndex] ?? 'partner';
  const isLastStep = stepIndex === WIZARD_STEPS.length - 1;

  const goNext = async (): Promise<void> => {
    const valid = await form.trigger([...STEP_FIELDS[step]]);
    if (valid) setStepIndex((index) => Math.min(index + 1, WIZARD_STEPS.length - 1));
  };

  // The summary renders on step change, so a snapshot is enough — and unlike `watch()` it does
  // not re-render every field of the wizard on every keystroke.
  const values = form.getValues();

  // The allowed-tarif list depends on which group tarif is selected, and both controls are on
  // one step, so this one field IS watched: a checkbox list that only refreshed on step change
  // would offer the previous product's tarifs until the operator navigated away and back.
  const selectedGroupTarif = form.watch('groupTarifErn');
  const selectedEntry = (available.data?.groupTarifs ?? []).find(
    (entry) => entry.ern === selectedGroupTarif,
  );
  const memberTarifs = selectedEntry?.memberTarifs ?? [];
  // The roles the chosen group tarif allows besides the policyholder (SDD-011 P-8) — the
  // agreement's own parties, not a member's.
  const furtherRoles = (selectedEntry?.partnerRoles ?? []).filter(
    (rule) => rule.roleErn !== POLICYHOLDER_ROLE,
  );
  const partnerValues = form.watch('partners') ?? {};
  const partnerRefusal = partnerRefusalOf(create.error, furtherRoles, partnerValues);

  return (
    <PageLayout maxWidth={860} header={<PageHeader title={t('groupPolicies.wizard.title')} />}>
      {/* The steps in one card, the footer right-aligned under a rule (design v2). */}
      <Paper variant="outlined" sx={(theme) => ({ p: 3, ...cardSurfaceSx(theme) })}>
        <Stepper activeStep={stepIndex} sx={{ mb: 3 }}>
          {WIZARD_STEPS.map((name) => (
            <Step key={name}>
              <StepLabel>{t(`groupPolicies.wizard.steps.${name}`)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 'partner' && (
          <FormSection
            bare
            title={t('groupPolicies.wizard.partner.title')}
            description={t('groupPolicies.wizard.partner.description')}
          >
            <Controller
              control={form.control}
              name="policyholderPartnerId"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('groupPolicies.wizard.partner.field')}
                  placeholder="PRT-leasingbank-01"
                  error={fieldState.error !== undefined}
                  helperText={
                    fieldState.error === undefined
                      ? t('groupPolicies.wizard.partner.fieldHint')
                      : translateValidationMessage(t, fieldState.error.message)
                  }
                  required
                />
              )}
            />
            <Controller
              control={form.control}
              name="code"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('groupPolicies.wizard.partner.codeField')}
                  helperText={t('groupPolicies.wizard.partner.codeHint')}
                />
              )}
            />
            <Controller
              control={form.control}
              name="effectiveFrom"
              render={({ field, fieldState }) => (
                <EffectiveDateSelector
                  label={t('groupPolicies.wizard.partner.validFrom')}
                  value={field.value === '' ? null : field.value}
                  onChange={(next) => {
                    field.onChange(next ?? '');
                  }}
                  locale={resolveFormattingLocale(i18n.language)}
                  required
                  helperText={
                    fieldState.error === undefined
                      ? t('groupPolicies.wizard.partner.validFromHint')
                      : translateValidationMessage(t, fieldState.error.message)
                  }
                />
              )}
            />
          </FormSection>
        )}

        {step === 'products' && (
          <FormSection
            bare
            title={t('groupPolicies.wizard.products.title')}
            description={t('groupPolicies.wizard.products.description')}
            columns={1}
          >
            <Controller
              control={form.control}
              name="groupTarifErn"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  onChange={(event) => {
                    // Changing the product invalidates the ticks: the tarifs one group tarif
                    // allows are not the tarifs another one does, and a checkbox left ticked
                    // would send an `allowedTarifs` entry the agreement cannot accept.
                    field.onChange(event);
                    form.setValue('allowedTarifs', []);
                  }}
                  label={t('groupPolicies.wizard.products.groupTarif')}
                  error={fieldState.error !== undefined}
                  helperText={
                    fieldState.error === undefined
                      ? t('groupPolicies.wizard.products.groupTarifHint')
                      : translateValidationMessage(t, fieldState.error.message)
                  }
                  required
                >
                  {(available.data?.groupTarifs ?? []).map((groupTarif) => (
                    <MenuItem key={groupTarif.ern ?? ''} value={groupTarif.ern ?? ''}>
                      {groupTarif.name ?? groupTarif.ern ?? ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={form.control}
              name="allowedTarifs"
              render={({ field, fieldState }) => (
                <Stack>
                  {memberTarifs.map((tarif) => {
                    const ern = tarif.ern ?? '';
                    return (
                      <FormControlLabel
                        key={ern}
                        control={
                          <Checkbox
                            checked={field.value.includes(ern)}
                            onChange={(event) => {
                              field.onChange(
                                event.target.checked
                                  ? [...field.value, ern]
                                  : field.value.filter((value) => value !== ern),
                              );
                            }}
                          />
                        }
                        label={tarif.name ?? ern}
                      />
                    );
                  })}
                  {fieldState.error !== undefined && (
                    <Typography variant="caption" color="error">
                      {translateValidationMessage(t, fieldState.error.message)}
                    </Typography>
                  )}
                </Stack>
              )}
            />
            {selectedGroupTarif === '' ? (
              <Typography variant="body2" color="text.secondary">
                {t('groupPolicies.wizard.partners.chooseFirst')}
              </Typography>
            ) : (
              furtherRoles.length > 0 && (
                <Controller
                  control={form.control}
                  name="partners"
                  render={({ field }) => (
                    <PartnerSection
                      rules={furtherRoles}
                      values={field.value ?? {}}
                      member={false}
                      refusal={partnerRefusal}
                      onChange={(roleErn, ids) => {
                        field.onChange({ ...(field.value ?? {}), [roleErn]: ids });
                      }}
                    />
                  )}
                />
              )
            )}
          </FormSection>
        )}

        {step === 'billing' && (
          <FormSection
            bare
            title={t('groupPolicies.wizard.billing.title')}
            description={t('groupPolicies.wizard.billing.description')}
          >
            <Controller
              control={form.control}
              name="billing.frequency"
              render={({ field }) => (
                <TextField {...field} select label={t('groupPolicies.overview.frequency')}>
                  {(['MONTHLY', 'QUARTERLY', 'ANNUAL'] as const).map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`groupPolicies.frequency.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={form.control}
              name="billing.mode"
              render={({ field }) => (
                <TextField {...field} select label={t('groupPolicies.overview.mode')}>
                  {(['AGGREGATE_CHILD_PREMIUMS', 'DIRECT'] as const).map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`groupPolicies.mode.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {/* Not editable: dunning is master-only by domain rule (SSOT I-20), so it is shown as
              a fact rather than offered as a choice that does not exist. */}
            <TextField
              label={t('groupPolicies.overview.dunning')}
              value={t('groupPolicies.overview.dunningValue')}
              disabled
            />
          </FormSection>
        )}

        {step === 'summary' && (
          <FormSection
            bare
            title={t('groupPolicies.wizard.summary.title')}
            description={t('groupPolicies.wizard.summary.description')}
            columns={1}
          >
            <Stack spacing={1.5}>
              <SummaryRow
                label={t('groupPolicies.overview.partnerId')}
                value={values.policyholderPartnerId}
              />
              <SummaryRow
                label={t('groupPolicies.wizard.partner.codeField')}
                value={
                  values.code === '' ? t('groupPolicies.wizard.partner.codeMinted') : values.code
                }
              />
              <SummaryRow
                label={t('groupPolicies.overview.validFrom')}
                value={
                  values.effectiveFrom === ''
                    ? '—'
                    : formatCivilDate(values.effectiveFrom, resolveFormattingLocale(i18n.language))
                }
              />
              <SummaryRow
                label={t('groupPolicies.wizard.products.groupTarif')}
                value={values.groupTarifErn || '—'}
              />
              <SummaryRow
                label={t('groupPolicies.overview.linkedProducts')}
                value={values.allowedTarifs.join(', ') || '—'}
              />
              {furtherRoles.map((rule) => (
                <SummaryRow
                  key={rule.roleErn ?? ''}
                  label={roleLabel(rule)}
                  value={
                    (values.partners?.[rule.roleErn ?? ''] ?? [])
                      .filter((id) => id.trim() !== '')
                      .join(', ') || '—'
                  }
                />
              ))}
              <SummaryRow
                label={t('groupPolicies.overview.frequency')}
                value={t(`groupPolicies.frequency.${values.billing.frequency}`)}
              />
              <SummaryRow
                label={t('groupPolicies.overview.mode')}
                value={t(`groupPolicies.mode.${values.billing.mode}`)}
              />
              <SummaryRow
                label={t('groupPolicies.overview.dunning')}
                value={t('groupPolicies.overview.dunningValue')}
              />
            </Stack>
            {create.isError && <Alert severity="error">{create.error.message}</Alert>}
          </FormSection>
        )}

        <Divider sx={{ my: 2.5 }} />
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}
        >
          <Button
            variant="text"
            onClick={() => {
              if (stepIndex === 0) onCancel();
              else setStepIndex((index) => index - 1);
            }}
          >
            {stepIndex === 0 ? t('groupPolicies.wizard.cancel') : t('groupPolicies.wizard.back')}
          </Button>
          {isLastStep ? (
            <Button
              variant="contained"
              onClick={() => {
                setConfirming(true);
              }}
            >
              {t('groupPolicies.wizard.finish')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                void goNext();
              }}
            >
              {t('groupPolicies.wizard.next')}
            </Button>
          )}
        </Stack>
      </Paper>

      <ConfirmAction
        open={confirming}
        title={t('groupPolicies.wizard.confirmTitle')}
        description={t('groupPolicies.wizard.confirmDescription')}
        confirmLabel={t('groupPolicies.wizard.finish')}
        busy={create.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          void form.handleSubmit((data) => {
            create.mutate(
              {
                ...(data.code === '' ? {} : { code: data.code }),
                // The agreement's partners per role (SDD-011): the policyholder from the partner
                // step, the further roles from the products step, validated by the service
                // against the group version's rules.
                partners: [
                  { roleErn: POLICYHOLDER_ROLE, partnerId: data.policyholderPartnerId },
                  ...linksOf(furtherRoles, data.partners ?? {}),
                ],
                groupTarifErn: data.groupTarifErn,
                allowedTarifs: data.allowedTarifs,
                billing: data.billing,
                effectiveFrom: data.effectiveFrom,
                // The agreement payload is validated against the schema the pinned group_core
                // publishes. Collecting it needs that schema, which is a form this wizard does
                // not build yet — so it starts empty rather than guessing at fields.
                data: {},
              },
              {
                onSuccess: (created) => {
                  setConfirming(false);
                  onCreated(created.groupPolicy?.code ?? '');
                },
              },
            );
          })();
        }}
      />
    </PageLayout>
  );
}

function SummaryRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): ReactNode {
  return (
    <Stack direction="row" spacing={2} useFlexGap sx={{ justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}
